import { useEffect, useMemo, useState } from 'react';

import Tippy from '@tippyjs/react';

import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Divider from 'components/shared/Divider/Divider';
import { isSubstepCompleted } from 'components/shared/OnboardingWizard/lib/resolveContinueAction';

const DATA_EXPORT_OPTIONS = {
  DATA_EXTRACTION_BEGUN: 'DATA_EXTRACTION_BEGUN',
  REQUEST_FULL_EXPORT: 'REQUEST_FULL_EXPORT',
  NO_DATA_IMPORT: 'NO_DATA_IMPORT'
};

const RADIO_OPTIONS = [
  {
    id: DATA_EXPORT_OPTIONS.DATA_EXTRACTION_BEGUN,
    label:
      'I confirm I have begun extracting my data (as acknowledged in the terms above and your agreement, it is the responsibility of the client to obtain and upload prior data)'
  },
  {
    id: DATA_EXPORT_OPTIONS.REQUEST_FULL_EXPORT,
    label:
      'I would like to request a full data export or format conversion from my previous system (I understand this is a monetized service and requires significant additional staff and technological resources)'
  },
  {
    id: DATA_EXPORT_OPTIONS.NO_DATA_IMPORT,
    label: 'I do not need mass data import or mapping at this time'
  }
];

const DataExport = ({
  addFooterActions,
  setHeaderButtonProps,
  onNext,
  isAdmin,
  orchestratorLoading,
  mutateOrchestratorData,
  orchestratorData,
  wizardState
}) => {
  const isCompleted = isSubstepCompleted(orchestratorData);

  const savedOption = useMemo(() => {
    const substepCode = orchestratorData?.substepCode;
    const steps = wizardState?.steps || [];
    for (const step of steps) {
      const substep = step.substeps?.find((s) => s.code === substepCode);
      if (substep?.data) {
        try {
          const parsed = typeof substep.data === 'string' ? JSON.parse(substep.data) : substep.data;
          return parsed?.dataExportOption || null;
        } catch {
          return null;
        }
      }
    }
    return null;
  }, [orchestratorData?.substepCode, wizardState?.steps]);

  const [selectedOption, setSelectedOption] = useState(savedOption);

  useEffect(() => {
    if (savedOption && !selectedOption) {
      setSelectedOption(savedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedOption]);

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick: () => {
          if (!selectedOption || isAdmin) {
            return onNext();
          }
          if (isCompleted && selectedOption === savedOption) return onNext();
          mutateOrchestratorData({ dataExportOption: selectedOption });
        },
        isDisabled: orchestratorLoading
      }
    ]);
  }, [
    orchestratorLoading,
    addFooterActions,
    setHeaderButtonProps,
    onNext,
    selectedOption,
    isCompleted,
    savedOption,
    mutateOrchestratorData
  ]);

  return (
    <div className="flex h-full w-full justify-center">
      <div className="flex w-full flex-col pb-5">
        <div className="rounded-[24px] border border-solid border-neutral-200 bg-white p-6">
          <div className="flex items-start gap-5 sm:flex-col">
            <div className="flex-shrink-0">
              <Icon icon="data-export" />
            </div>
            <div className="flex flex-col gap-4">
              <h2 className="text-xl font-semibold text-neutral-800">Request a full data export</h2>
              <p className="text-sm leading-relaxed text-neutral-600">
                Please extract the most comprehensive option available. Most software provides CSV
                and PDF exports for a basic transfer. However, if you want more data mapped to your
                charts such as insurances, past appointments, and clinical data, please request bulk
                export software. It differs between EHRs, and most will export it as a ZIP file.
                Once you receive the ZIP file from your previous EHR, upload it from the selector in
                this wizard and we&apos;ll take it from there.
              </p>
              <p className="text-sm leading-relaxed text-neutral-600">
                We only upload the data we get from the upload which may not include all of the data
                you may expect. Please make sure you request all of the data to be exported and as
                fresh as possible.
              </p>

              <Divider />
              <div
                className="flex flex-col gap-4"
                role="radiogroup"
                aria-label="Data export options">
                {RADIO_OPTIONS.map((option) => {
                  const optionLabel = (
                    <label
                      className={cn(
                        'flex items-start gap-3',
                        isAdmin ? 'cursor-not-allowed' : 'cursor-pointer'
                      )}>
                      <input
                        type="radio"
                        name="dataExportOption"
                        value={option.id}
                        disabled={isAdmin}
                        checked={selectedOption === option.id}
                        onChange={() => setSelectedOption(option.id)}
                        className="sr-only"
                      />
                      <div className="mt-0.5 flex-shrink-0" aria-hidden="true">
                        <div
                          className={`mt-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-solid ${
                            selectedOption === option.id
                              ? 'border-primary-500'
                              : 'border-neutral-300'
                          }`}>
                          {selectedOption === option.id && (
                            <div className="h-2.5 w-2.5 rounded-full bg-primary-500" />
                          )}
                        </div>
                      </div>
                      <span
                        className={cn(
                          'text-sm leading-relaxed text-neutral-700',
                          isAdmin && 'text-neutral-400'
                        )}>
                        {option.label}
                      </span>
                    </label>
                  );

                  if (!isAdmin) {
                    return <div key={option.id}>{optionLabel}</div>;
                  }

                  return (
                    <Tippy
                      key={option.id}
                      content="This step must be completed by a user. Admins cannot acknowledge it."
                      placement="left"
                      className="tippy-dark">
                      <span className="block">{optionLabel}</span>
                    </Tippy>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExport;
